import { useMemo } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import BaseProgressBar from "@/components_v2/ProgressBars/BaseProgressBar";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { SurveyType } from "@/shared/utils/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { SurveyHeaderProps } from "@/features/survey/summary/types";
import AddDocumentIcon from "@/assets/icons/add-document.svg?react";
import PreviewIcon from "@/assets/icons/preview.svg?react";
import SendIcon from "@/assets/icons/send.svg?react";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";

import "../styles.css";

const SurveyHeader = ({
  survey,
  statistics,
  setOpenConfirmationModal,
  isSample,
}: SurveyHeaderProps) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const { canSubmit } = useSurveyPermissions(survey, currentUser);

  const progressBarData = useMemo(() => {
    const totals = statistics.reduce(
      (acc, section) => ({
        answeredOptional:
          acc.answeredOptional + section.answeredOptionalQuestionsCount,
        answeredRequired:
          acc.answeredRequired + section.answeredRequiredQuestionsCount,
        totalOptional: acc.totalOptional + section.optionalQuestionsCount,
        totalRequired: acc.totalRequired + section.requiredQuestionsCount,
      }),
      {
        answeredOptional: 0,
        answeredRequired: 0,
        totalOptional: 0,
        totalRequired: 0,
      },
    );

    const totalAnswered = totals.answeredOptional + totals.answeredRequired;
    const totalQuestions = totals.totalOptional + totals.totalRequired;
    const percentage =
      totalQuestions > 0
        ? Math.round((totalAnswered / totalQuestions) * 100)
        : 0;

    return {
      totalQuestions,
      totalAnsweredQuestions: totalAnswered,
      percentage,
      label: t("survey.labels.progressLabel", {
        answeredRequired: totals.answeredRequired,
        totalRequired: totals.totalRequired,
        answeredOptional: totals.answeredOptional,
        totalOptional: totals.totalOptional,
      }),
      helpText: t("survey.labels.percentComplete", { percentage }),
      description: t("survey.labels.overallProgressDescription", {
        answered: totalAnswered,
        total: totalQuestions,
      }),
    };
  }, [statistics]);

  return (
    <div className="survey-sections-overview-header-container flex flex-col gap-400 items-start full-width">
      <div className="flex flex-wrap gap-300 items-center justify-between full-width">
        <h1 className="survey-sections-overview-heading header-l-medium">
          {isSample
            ? survey.title + " - " + t("survey.labels.sample")
            : survey.title}
        </h1>
        {!isSample && (
          <h3 className="survey-sections-overview-additional-info header-s-regular">
            <span>{t("survey.labels.reportingPeriod")}: </span>
            <span>
              {DateTime.fromISO(survey.startDate).toFormat("dd MMM yyyy")}{" "}
              -{" "}
            </span>
            <span>
              {DateTime.fromISO(
                survey.softDeadline || survey.hardDeadline,
              ).toFormat("dd MMM yyyy")}
            </span>
          </h3>
        )}
      </div>

      <p className="survey-sections-overview-content body-m-regular">
        {survey.description}
      </p>

      {progressBarData ? (
        <BaseProgressBar
          value={progressBarData.totalAnsweredQuestions}
          total={progressBarData.totalQuestions}
          label={progressBarData.label}
          helpText={progressBarData.helpText}
          description={progressBarData.description}
        />
      ) : (
        <BaseSpinner size="small" />
      )}

      <div className="survey-sections-overview-actions-container flex flex-wrap gap-300 items-center full-width">
        {!isSample && (
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            leadingIcon={
              <BaseSvgRenderer
                width="20"
                height="20"
                component={AddDocumentIcon}
              />
            }
            onClick={() => window.open(`${location.pathname}/sample`, "_blank")}
          >
            {t("survey.labels.sampleSurvey", {
              surveyType: SurveyType.getLabel(survey.type),
            })}
          </BaseButton>
        )}

        {!isSample && (
          <div className="flex flex-wrap gap-250">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer
                  width="20"
                  height="20"
                  component={PreviewIcon}
                />
              }
              type={BUTTON_TYPES.LINK}
              to={`/reports/${survey.id}/overview`}
            >
              {t("survey.labels.previewSurvey")}
            </BaseButton>

            <BaseTooltip
              title={canSubmit ? "" : t("survey.labels.onlyAdminsCanSubmit")}
            >
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                disabled={!canSubmit}
                leadingIcon={
                  <BaseSvgRenderer
                    width="20"
                    height="20"
                    component={SendIcon}
                  />
                }
                onClick={() => setOpenConfirmationModal(true)}
              >
                {t("survey.labels.submitSurvey")}
              </BaseButton>
            </BaseTooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyHeader;
